import * as React from 'react'
import { ArrowUpRight, MessageCircleQuestion, Trash2 } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import {
  activeSections,
  ingestApi,
  isIngestJobFinished,
  isIngestJobNotFoundError,
  setSidebarFocus,
  useAppDispatch,
  useGetAuthzMeQuery,
  useGetBranchQuery,
  useGetIngestConversationQuery,
  useGetIngestQuestionsQuery,
  useGetUnstructuredJobDetailQuery,
  useIngestCotStream,
  useListIngestSectionsQuery,
  useStopIngestJobMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { useDebugMode } from '../../lib/debug-mode'
import { cn } from '../../lib/utils'
import { requestRailExpandOnNextMount } from '../Chat/chat-handoff'
import { currentIngestTitle } from '../Chat/CurrentIngestEntry'
import {
  statusToLabel,
  statusToPill,
  type IngestJobState
} from '../../pages/IngestPage/util/jobStatusMap'
import { WriteButton } from '../../pages/IngestPage/shared/WriteButton'
import { StatusPill } from '../primitives/StatusPill'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { documentUploadCopy, documentUploadModeForTarget } from './DocumentUploadDialog'
import { useDocumentUpload } from './DocumentUploadProvider'
import { Conversation } from './Conversation'
import { IngestAskLane } from './IngestAskLane'
import { IngestJobDeleteDialog } from './IngestJobDeleteDialog'
import {
  DEFAULT_INGEST_CHAT_MODE,
  IngestChatModeTabs,
  type IngestChatMode
} from './IngestChatModeTabs'
import { newestPendingQuestionId } from '../Chat/sessionTabRules'
import {
  INGEST_QUESTION_PARAM,
  INGEST_SESSION_FOCUS_STATE,
  ingestFullSessionPath,
  ingestQuestionElementId
} from './ingest-session-route'

function jobEndStatusFromState(
  state: string | undefined
): 'done' | 'failed' | 'aborted' | undefined {
  if (state === 'error') return 'failed'
  if (state === 'cancelled') return 'aborted'
  if (state === 'done') return 'done'
  return undefined
}

interface IngestSessionPaneProps {
  jobId: string
  layout: 'rail' | 'page'
  /**
   * When false, hide Add docs / Merge / Delete in the pane chrome — the host
   * (e.g. AgentIngestPage `RunSpine`) already owns those actions. Rail keeps
   * the default so Current Ingest stays self-contained.
   */
  showSessionActions?: boolean
  /** Page-layout section title. Defaults to "Conversation". */
  heading?: string
}

export function IngestSessionPane({
  jobId,
  layout,
  showSessionActions = true,
  heading = 'Conversation'
}: IngestSessionPaneProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [jobFinished, setJobFinished] = React.useState(false)
  const [stopJob, { isLoading: stopping }] = useStopIngestJobMutation()
  const [stopError, setStopError] = React.useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [chatTab, setChatTab] = React.useState<IngestChatMode>(DEFAULT_INGEST_CHAT_MODE)
  const wasTerminalRef = React.useRef(false)
  const { openDocumentUpload, documentTransferOpen } = useDocumentUpload()
  const canManage = useGetAuthzMeQuery().data?.permissions.includes('ingest/manage') === true
  const debugMode = useDebugMode()

  const [jobDeleted, setJobDeleted] = React.useState(false)
  const { data: job, error: jobError } = useGetUnstructuredJobDetailQuery(jobId, {
    skip: !jobId,
    refetchOnMountOrArgChange: true
  })
  React.useEffect(() => {
    setJobDeleted(isIngestJobNotFoundError(jobError))
  }, [jobError])
  const live = !jobFinished && !jobDeleted
  const { data: branch, isError: branchUnavailable } = useGetBranchQuery(job?.branchId ?? '', {
    skip: !job?.branchId
  })

  React.useEffect(() => {
    if (!job) return
    const terminal = isIngestJobFinished(job.state)
    setJobFinished(terminal)
    // Refresh the sidebar session list so Current Ingest flips off "running"
    // without waiting for the jobs wake-up.
    if (terminal && !wasTerminalRef.current) {
      dispatch(ingestApi.util.invalidateTags(['UnstructuredJobs']))
    }
    wasTerminalRef.current = terminal
  }, [dispatch, job])

  const { data: questions } = useGetIngestQuestionsQuery(jobId, {
    skip: !jobId || jobDeleted,
    refetchOnMountOrArgChange: true
  })
  const conversationId = job?.conversationId ?? ''
  const { data: conversation } = useGetIngestConversationQuery(conversationId, {
    skip: !conversationId || jobDeleted,
    refetchOnMountOrArgChange: true
  })
  // Like AgentIngestPage section tabs — create_section must show a pill on the
  // next job wake-up without waiting for a nested parser run / SSE tail.
  const { data: sectionRows } = useListIngestSectionsQuery(jobId, {
    skip: !jobId || jobDeleted,
    refetchOnMountOrArgChange: true
  })
  const sections = React.useMemo(() => activeSections(sectionRows), [sectionRows])
  const cotStream = useIngestCotStream(jobId, live)
  // Transcripts publish no wake-up of their own; a committed lead turn is
  // exactly when this one changes. `committed` is capped, so key on its tail.
  const lastCommittedTurn = cotStream.committed[cotStream.committed.length - 1]
  React.useEffect(() => {
    if (!lastCommittedTurn || !conversationId) return
    dispatch(ingestApi.util.invalidateTags([{ type: 'IngestConversation', id: conversationId }]))
  }, [conversationId, dispatch, lastCommittedTurn])

  const pending = questions?.pending ?? []
  const answered = questions?.answered ?? []
  const messages = questions?.messages ?? []
  const pendingQuestionCount = pending.length
  const linkedQuestionId = searchParams.get(INGEST_QUESTION_PARAM)?.trim() || null
  const focusQuestionId = linkedQuestionId ?? newestPendingQuestionId(pending)
  const active = !jobFinished
  const jobEndStatus = jobEndStatusFromState(job?.state) ?? 'done'
  const state = (job?.state ?? 'processing') as IngestJobState
  const needsInput = pendingQuestionCount > 0 && active
  const pillStatus = needsInput ? 'warn' : statusToPill[state]
  const pillLabel = needsInput ? 'needs input' : statusToLabel[state]

  const focusQuestion = React.useCallback((writeId: string | null): void => {
    setChatTab('agent')
    if (!writeId) return
    const reveal = (): void => {
      const node = document.getElementById(ingestQuestionElementId(writeId))
      node?.scrollIntoView({ block: 'center' })
      node?.focus()
    }
    window.requestAnimationFrame(reveal)
  }, [])

  React.useEffect(() => {
    if (!linkedQuestionId) return
    setChatTab('agent')
  }, [linkedQuestionId])

  React.useEffect(() => {
    if (chatTab !== 'agent' || !linkedQuestionId) return
    focusQuestion(linkedQuestionId)
  }, [chatTab, focusQuestion, linkedQuestionId])

  const documentMode = documentUploadModeForTarget(
    job?.state,
    branch,
    branchUnavailable || Boolean(job && !job.branchId)
  )
  const canReuseJobForDocuments = documentMode === 'add'
  // Compact labels so the rail actions stay on one line.
  const documentActionLabel = documentMode === 'add' ? 'Add docs' : 'Ingest'
  const documentContextJobId = canReuseJobForDocuments ? jobId : undefined
  const canReview = state === 'done' && !!job?.branchId

  const stopIngest = (): void => {
    if (!jobId || stopping) return
    void stopJob(jobId)
      .unwrap()
      .then(() => setStopError(null))
      .catch(() => {
        // Polling refetch surfaces the authoritative server state; keep a local
        // hint so a failed click is not silent.
        setStopError('Stop failed — the job may still be running.')
      })
  }

  const openAddDocuments = React.useCallback(
    (files?: File[]) => {
      if (documentTransferOpen) return
      openDocumentUpload({
        mode: documentMode,
        jobId,
        ...(documentContextJobId ? { contextJobId: documentContextJobId } : {}),
        ...(files && files.length > 0 ? { seedFiles: files } : {})
      })
    },
    [documentContextJobId, documentMode, documentTransferOpen, jobId, openDocumentUpload]
  )

  const handleDropDocuments = React.useCallback(
    (files: File[]) => {
      openAddDocuments(files)
    },
    [openAddDocuments]
  )

  const openMergeReview = (): void => {
    if (!job?.branchId) return
    const params = new URLSearchParams({
      source: job.branchId,
      returnTo: `/ingest/jobs/${jobId}/agent`,
      returnLabel: 'Ingest job'
    })
    navigate(`/branches/merge?${params.toString()}`)
  }

  // Same handoff as a fresh unstructured upload: AgentIngestPage focuses the
  // Current Ingest rail once the job's branch is active. Leaving `/chat`
  // remounts that rail, so also expand it — otherwise the tab switch is
  // invisible on viewports that default collapsed.
  const fullSessionPath = ingestFullSessionPath(jobId)
  const handleOpenFullSession = (): void => {
    requestRailExpandOnNextMount()
    dispatch(setSidebarFocus({ kind: 'session', id: jobId }))
  }

  const addDocumentsButton = (
    <Button
      type="button"
      variant="outline"
      size="xs"
      className="h-auto px-2 py-1 text-xs"
      disabled={documentTransferOpen}
      title={documentUploadCopy(documentMode).title}
      onClick={() => openAddDocuments()}
    >
      {documentActionLabel}
    </Button>
  )

  const reviewMergeButton = (
    <Button
      type="button"
      variant={canReview ? 'default' : 'outline'}
      size="xs"
      className="h-auto px-2 py-1 text-xs"
      disabled={!canReview}
      title={canReview ? 'Review the branch and merge' : 'Available once the run finishes'}
      onClick={openMergeReview}
    >
      Merge
    </Button>
  )

  const openFullSessionButton = (
    <Button
      asChild
      variant="ghost"
      size="xs"
      className="h-auto w-fit px-2 py-1 text-xs text-muted-foreground"
    >
      <Link to={fullSessionPath} state={INGEST_SESSION_FOCUS_STATE} onClick={handleOpenFullSession}>
        Open full session
        <ArrowUpRight className="size-3" />
      </Link>
    </Button>
  )

  const deleteJobButton = debugMode ? (
    <WriteButton
      canManage={canManage}
      type="button"
      variant="outline"
      size="xs"
      className="h-auto border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive hover:bg-destructive/20 hover:text-destructive"
      title="Delete this ingest job"
      onClick={() => setDeleteDialogOpen(true)}
    >
      <Trash2 className="size-3" />
      Delete
    </WriteButton>
  ) : null

  if (jobDeleted) {
    return (
      <p
        role="status"
        className={cn(
          'm-0 text-xs text-muted-foreground',
          layout === 'rail' ? 'px-4 py-3' : 'px-0.5'
        )}
      >
        This ingest job was deleted.
      </p>
    )
  }

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col',
        layout === 'rail' ? 'h-full min-h-0 flex-1 overflow-hidden' : 'gap-2.5'
      )}
    >
      {layout === 'rail' ? (
        <header className="flex shrink-0 flex-col gap-2 border-b px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h2
                className="m-0 min-w-0 truncate text-sm font-semibold text-foreground"
                title={currentIngestTitle(job?.runName, job?.fileName)}
              >
                {currentIngestTitle(job?.runName, job?.fileName)}
              </h2>
              <StatusPill status={pillStatus} className="shrink-0">
                {pillLabel}
              </StatusPill>
            </div>
            {openFullSessionButton}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <IngestChatModeTabs
              value={chatTab}
              onChange={setChatTab}
              agentBadgeCount={pendingQuestionCount}
              ariaLabel="Ingest chat mode"
              hint={false}
              size="sm"
              className="border-b-0 py-0"
            />
            {showSessionActions ? (
              <div className="ml-auto flex shrink-0 items-center gap-2">
                {deleteJobButton}
                {addDocumentsButton}
                {reviewMergeButton}
              </div>
            ) : null}
          </div>
        </header>
      ) : (
        <div className="flex items-center gap-2 px-0.5">
          <h2 className="m-0 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
            {heading}
          </h2>
          {pendingQuestionCount > 0 ? (
            <Badge className="border-transparent bg-st-warn-bg text-st-warn">
              {pendingQuestionCount} needs you
            </Badge>
          ) : null}
          {showSessionActions ? (
            <div className="ml-auto flex items-center gap-2">
              {deleteJobButton}
              {addDocumentsButton}
              {reviewMergeButton}
            </div>
          ) : null}
        </div>
      )}

      {stopError ? (
        <p className="m-0 px-4 text-xs text-destructive" role="alert">
          {stopError}
        </p>
      ) : null}

      {pendingQuestionCount > 0 ? (
        <button
          type="button"
          className="w-full shrink-0 border-b border-st-warn/40 bg-st-warn-bg/50 py-2 text-left text-xs text-st-warn"
          onClick={() => focusQuestion(focusQuestionId)}
        >
          <span className={cn('flex items-center gap-2 px-4', layout === 'page' && 'px-0.5')}>
            <MessageCircleQuestion className="size-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              {pendingQuestionCount === 1
                ? 'An agent is waiting on your answer'
                : `${pendingQuestionCount} agents are waiting on your answers`}
              {chatTab === 'ask' ? ' — open Agent tab' : ''}
            </span>
          </span>
        </button>
      ) : null}

      {layout === 'rail' ? null : (
        <IngestChatModeTabs
          value={chatTab}
          onChange={setChatTab}
          agentBadgeCount={pendingQuestionCount}
          ariaLabel="Ingest chat mode"
          size="sm"
          hint={false}
          className="px-0.5 py-2"
        />
      )}

      {/* Keep both panes mounted so Ask↔Agent switches don't remount /
          refetch and flash empty transcripts. */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden',
          chatTab !== 'ask' && 'hidden'
        )}
      >
        <IngestAskLane key={jobId} jobId={jobId} />
      </div>
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden',
          chatTab !== 'agent' && 'hidden'
        )}
      >
        <Conversation
          jobId={jobId}
          branchId={job?.branchId}
          pending={pending}
          answered={answered}
          messages={messages}
          transcript={conversation?.runs}
          sections={sections}
          byPill={cotStream.byPill}
          liveCot={cotStream.liveText}
          liveCommitted={cotStream.committed}
          fileName={job?.fileName}
          startedAt={job?.submittedAt}
          additionalContext={
            typeof job?.payload?.['additionalContext'] === 'string'
              ? job.payload['additionalContext']
              : undefined
          }
          active={active}
          jobEndStatus={jobEndStatus}
          layout={layout}
          focusQuestionId={focusQuestionId}
          onStop={stopIngest}
          stopping={stopping}
          canManage={canManage}
          documentTransferOpen={documentTransferOpen}
          onDropDocuments={handleDropDocuments}
        />
      </div>

      <IngestJobDeleteDialog
        jobId={jobId}
        fileName={job?.fileName}
        branchId={job?.branchId}
        jobState={state}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={() => navigate('/ingest')}
      />
    </div>
  )
}

export default IngestSessionPane
